import type { Header } from '@tanstack/react-table';
import type { ColumnMetadata, LookupDataItem } from '../../../types';
import type { FilterOperators, FilterMenuAnchor } from '../types';
import { isBooleanColumn, isDateColumn, isNumberColumn, isUtilityGridColumn } from '../utils';
import { BooleanFilterInput } from './BooleanFilterInput';
import { DateFilterInput } from './DateFilterInput';
import { NumberFilterInput } from './NumberFilterInput';
import { LookupFilterInput } from './LookupFilterInput';
import { TextFilterInput } from './TextFilterInput';
import './filterInputs.css';

type FilterCellProps<T> = {
  header: Header<T, unknown>;
  columnMetadata?: ColumnMetadata;
  filterValue: string;
  filterOperators: FilterOperators;
  setFilterMenuAnchor: React.Dispatch<React.SetStateAction<FilterMenuAnchor>>;
  lookupMaps?: Map<string, Record<string, string>>;
  rawLookupItems?: Map<string, LookupDataItem[]>;
  manualFiltering?: boolean;
};

/**
 * Filter cell component that routes to the appropriate filter input
 * based on column type (boolean, date, number, lookup, or text)
 */
export const FilterCell = <T,>({
  header,
  columnMetadata,
  filterValue,
  filterOperators,
  setFilterMenuAnchor,
  lookupMaps,
  rawLookupItems,
  manualFiltering,
}: FilterCellProps<T>) => {
  const columnId = header.column.id;

  if (isUtilityGridColumn(columnId)) {
    return null;
  }

  // Check for lookup map
  const fieldName = columnMetadata?.field || columnId;
  const lookupMap = lookupMaps?.get(fieldName);

  if (lookupMap) {
    return (
      <LookupFilterInput
        header={header}
        filterValue={filterValue}
        lookupMap={lookupMap}
        lookupItems={rawLookupItems?.get(fieldName)}
        manualFiltering={manualFiltering}
      />
    );
  }

  // Boolean filter
  if (isBooleanColumn(columnMetadata)) {
    return <BooleanFilterInput header={header} filterValue={filterValue} />;
  }

  // Date filter
  if (isDateColumn(columnMetadata)) {
    return (
      <DateFilterInput
        header={header}
        columnMetadata={columnMetadata}
        filterValue={filterValue}
        filterOperators={filterOperators}
        setFilterMenuAnchor={setFilterMenuAnchor}
      />
    );
  }

  // Number filter
  if (isNumberColumn(columnMetadata)) {
    return (
      <NumberFilterInput
        header={header}
        filterValue={filterValue}
        filterOperators={filterOperators}
        setFilterMenuAnchor={setFilterMenuAnchor}
      />
    );
  }

  // Text filter (default)
  return (
    <TextFilterInput
      header={header}
      filterValue={filterValue}
      filterOperators={filterOperators}
      setFilterMenuAnchor={setFilterMenuAnchor}
    />
  );
};
